import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Close, WarningAmber } from "@mui/icons-material";
import { keyframes } from "@mui/system";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "¿Estás seguro?",
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  variant = "danger", // danger, warning, info
}) {
  const variantConfig = {
    danger: {
      primary: "#dc2626",
      light: "#fef2f2",
      gradient: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
      icon: "#ef4444",
    },
    warning: {
      primary: "#f59e0b",
      light: "#fffbeb",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      icon: "#fbbf24",
    },
    info: {
      primary: "#3b82f6",
      light: "#eff6ff",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      icon: "#60a5fa",
    },
  };

  const config = variantConfig[variant] || variantConfig.danger;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "visible",
          position: "relative",
          animation: `${fadeIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`,
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: config.gradient,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
        }}
      />

      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          transition: "all 0.2s",
          "&:hover": {
            color: config.primary,
            transform: "rotate(90deg)",
            backgroundColor: config.light,
          },
        }}
      >
        <Close fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 4, pt: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: config.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              animation: `${pulse} 2s ease-in-out infinite`,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -8,
                borderRadius: "50%",
                background: config.gradient,
                opacity: 0.1,
                animation: `${pulse} 2s ease-in-out infinite`,
                animationDelay: "0.5s",
              },
            }}
          >
            <WarningAmber
              sx={{
                fontSize: 42,
                color: config.icon,
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 2,
            animation: `${slideUp} 0.4s ease-out 0.1s both`,
            fontFamily: "'Inter', -apple-system, sans-serif",
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{
            mb: 4,
            lineHeight: 1.6,
            animation: `${slideUp} 0.4s ease-out 0.1s both`,
            fontSize: "0.95rem",
          }}
        >
          {message}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            animation: `${slideUp} 0.4s ease-out 0.1s both`,
          }}
        >
          <Button
            onClick={onClose}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 600,
              borderColor: "divider",
              color: "text.primary",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: config.primary,
                backgroundColor: config.light,
                transform: "translateY(-1px)",
              },
            }}
          >
            {cancelText}
          </Button>

          <Button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
            disableElevation
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 600,
              background: config.gradient,
              color: "white",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 20px ${config.primary}40`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            {confirmText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
